<template>
  <div class="assessment-container">
    <div class="filter-sec">
      <el-form class="filter-form">
        <elsa-select 
          :options="filterOptions"
          :placeholder="$t('classes.classes_details.students_section.assessment_filter')"
          name="assessment-filter"
          @change="changeAssessment"
          v-model.lazy="assessmentType"
          filterable
        />
      </el-form>
    </div>
    <div class="export-assessment">
      <class-assessment-exporter 
        button-type="primary" 
        :usersSelected="selectedDataSource" 
        :startDate="startDate" 
        :canExport="canExport" 
        :endDate="endDate"
        :assessmentType="assessmentType"
      />
    </div>
    <date-interval-picker
      class="date-interval-sec"
      :scope="scope"
      :showScope="false"
      :endDate="endDate"
      :startDate="startDate"
      :timelineLabel="timelineLabel"
      @updatedDate="updatedDate"
      @updateScope="updateScope"
    />
    <table-layout
      v-if="latestAssessments.length > 0 && isSpecialAssessmentTest === false"
      :showTitle="false"
      :pager-counter="pageCounter"
      :pageSize="numberElementsPerPage"
      :total="pagination.total"
      :currentChange="getDataSetPage"
      :current-page.sync="currentPage"
      :queryString.sync="queryString"
      :selected-data-source.sync="selectedDataSource"
      :query-placeholder="$t('common.search')"
      :search="searchData"
      :title="$t('study_sets.curriculum')"
      :empty-text="$t('common.no_values')"
      :dataSource="latestAssessments"
      :is-show-pagination-text="false"
      @cell-mouse-enter="handleHoveringCell"
      @cell-mouse-leave="clearHoveringCell"
      v-loading="loading"
      ref="table"
    >
      <template slot="cols">
        <el-table-column sortable :sort-method="sortName" :label="$t($i18nLabelsHelper(organizationType, 'student_name'))" width="200">
          <template slot-scope="scope">
            <div class="user-info">
              <div class="owner-info">
                <router-link :to="`/organization/${getCurrentClass.organization_id}/students/${scope.row.user.id}`">
                  <p>{{ name(scope.row.user) }}</p>
                  <p>{{ scope.row.user.email }}</p>
                </router-link>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          sortable
          :label="$t('classes.classes_details.assessment.table.date')"
          prop="last_assessment.date"
          align="center"
          width="230"
          :formatter="(row, colunm, cellValue) => formatDate(row.last_assessment.date || 0)"
          >
        </el-table-column>
        <el-table-column sortable :label="$t('classes.classes_details.assessment.table.eps')" align="center" width="250">
          <template slot-scope="scope">
            <span >
              {{ formatScore(scope.row.last_assessment.eps) }}
              <el-button
                class="assessment-detail-btn"
                v-if="canShowButton(scope)"
                @click.native.prevent="handleShowAssessment(scope.row.user)">
                {{$t('classes.classes_details.assessment.table.btn_history')}}
              </el-button>
            </span>
          </template>
        </el-table-column>
        <el-table-column
          sortable
          prop="last_assessment.ons"
          align="center"
          :formatter="percentageFormatter"
          :label="$t('classes.classes_details.students_section.pronunciation')"
        />
        <el-table-column
          sortable
          prop="last_assessment.sis"
          align="center"
          :formatter="percentageFormatter"
          :label="$t('classes.classes_details.students_section.intonation')"
        />
        <el-table-column
          sortable
          prop="last_assessment.fls"
          align="center"
          :formatter="percentageFormatter"
          :label="$t('classes.classes_details.students_section.fluency')"
        />
      </template> 
    </table-layout>
    <table-layout
      v-else
      :pager-counter="pageCounter"
      :pageSize="numberElementsPerPage"
      :total="pagination.total"
      :currentChange="getDataSetPage"
      :current-page.sync="currentPage"
      :showTitle="false"
      :empty-text="$t('common.no_values')"
      :dataSource="latestAssessments"
      v-loading="loading"
      @cell-mouse-enter="handleHoveringCell"
      @cell-mouse-leave="clearHoveringCell"
      ref="table"
    >
      <template slot="cols">
        <el-table-column sortable :sort-method="sortName" :label="$t($i18nLabelsHelper(organizationType, 'student_name'))" width="200">
          <template slot-scope="scope">
            <div class="user-info">
              <div class="owner-info">
                <router-link :to="`/organization/${getCurrentClass.organization_id}/students/${scope.row.user.id}`">
                  <p>{{ name(scope.row.user) }}</p>
                  <p>{{ scope.row.user.email }}</p>
                </router-link>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          sortable
          :label="$t('classes.classes_details.assessment.table.date')"
          prop="last_assessment.date"
          align="center"
          width="230"
          :formatter="(row, colunm, cellValue) => formatDate(row.last_assessment.date || 0)"
          >
        </el-table-column>
        <el-table-column
          sortable
          prop="last_assessment.overall"
          align="center"
          width="190"
          :label="$t('classes.classes_details.students_section.assessment_test')"
        >
          <template slot-scope="scope">
            <span >
              {{ scope.row.last_assessment.overall }}
              <el-button
                class="assessment-detail-btn"
                v-if="canShowButtonAdditionalScore(scope)"
                @click.native.prevent="handleShowAssessment(scope.row.user)">
                {{$t('classes.classes_details.assessment.table.btn_history')}}
              </el-button>
            </span>
          </template>
        </el-table-column>
        <el-table-column
          sortable
          prop="last_assessment.pronunciation"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.pronunciation')"
        />
        <el-table-column
          sortable
          prop="last_assessment.intonation"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.intonation')"
        />
        <el-table-column
          sortable
          prop="last_assessment.word_linkage"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.word_linkage')"
        />
        <el-table-column
          sortable
          prop="last_assessment.ending_sounds"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.ending_sounds')"
        />
        <el-table-column
          sortable
          prop="last_assessment.pausing"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.pausing')"
        />
        <el-table-column
          sortable
          prop="last_assessment.pace"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.pace')"
        />
        <el-table-column
          sortable
          prop="last_assessment.text"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.text')"
        />
        <el-table-column
          sortable
          prop="last_assessment.volume"
          align="center"
          width="180"
          :label="$t('classes.classes_details.students_section.volume')"
        />
      </template> 
    </table-layout>
    <assessments-history
      :title="$t('common.assessment-history')"
      :dialogVisible="showAssessmentModal"
      @closed="showAssessmentModal = false"
      :assessments="assessmentsUser"
      :assessmentType="assessmentType"
    />
  </div>
</template>

<script>
import ClassAssessmentExporter from "@/components/common/ClassAssessmentExporter"
import DateIntervalPicker from '@/components/common/DateIntervalPicker'
import AssessmentsHistory from "@/components/modals/reports/AssessmentsHistory";
import TableLayout from '@/components/common/v2/ElsaBaseTableLayout'
import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER, DATE_FORMAT } from "@/constants/generic"
import { always, is, union, without, unless, isNil } from 'ramda'
import  { mapState, mapActions, mapGetters } from 'vuex'
import { utcToZonedTime } from 'date-fns-tz'
import { format } from 'date-fns'
import { warningMessage } from "@/utils/messages";
import percentage from '@/components/common/mixins/percentage'
import ElsaSelect from '@/components/common/Select'
export default {
    props: ['organizationType', 'startDate', 'endDate', 'scope', 'timelineLabel', 'filterOptions', 'hasSpecialAssessmentTest', 'groupId'],
    components: {
      ClassAssessmentExporter,
      DateIntervalPicker,
      TableLayout,
      AssessmentsHistory,
      ElsaSelect
    },
    data() {
      return {
        currentPage: 1,
        queryString: null,
        loading: true,
        selectedDataSource: [],
        hoveringId: "",
        showAssessmentModal: false,
        assessmentType: this.hasSpecialAssessmentTest?'sgd-assessment':'general'
      }
    },
    computed: {
      ...mapState('classes', [
        'studentActivity',
        'assessmentsUser',
      ]),
      ...mapGetters({
        pagination: 'classes/buildPaginationLatestAssessment',
        latestAssessments: 'classes/getLatestAssessments',
        currentOrganization: 'organization/currentOrganization',
        getCurrentClass: 'classes/getCurrentClass'
      }),
      canExport() {
        return true
      },
      startDateAndEndDate() {
        return `${this.startDate}|${this.endDate}`;
      },
      pageCounter: always(PAGE_COUNTER),
      numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE),
      isSpecialAssessmentTest() {
        return this.assessmentType=='sgd-assessment'
      },
    },
    watch: {
      startDateAndEndDate() {
        this.$store.dispatch('classes/reset_latest_assessments')
        this.getDatas({page: 1})
      }
    },
    methods: {
      ...mapActions('classes', ['getLatestAssessments', 'getAssessmentUser', 'resetAssessmentUser']),
      updatedDate(dateObject) {
        return this.$emit('updatedDate', dateObject)
      },
      updateScope(newScope) {
        return this.$emit('updateScope', newScope)
      },
      getDataSetPage(page) {
        return this.getDatas({ page })
      },
      async getDatas({ page = 1}) {
        this.loading = true
        const classId = this.groupId;
        let params = {from: this.startDate, to: this.endDate, assessment_type: this.assessmentType, page, classId }
        await this.getLatestAssessments(params)
        this.selectedDataSource = []
        this.loading = false
      },
      searchData() {
        return
      },
      sortName(rowA, rowB) {
        const nameAConstructable = rowA.user.first_name || rowA.user.last_name
        const nameBConstructable = rowB.user.first_name || rowB.user.last_name
        // Neu ca 2 deu co first name hoac last name
        if (nameAConstructable && nameBConstructable) {
          const nameA = rowA.user.first_name + ' ' + rowA.user.last_name
          const nameB = rowB.user.first_name + ' ' + rowB.user.last_name
          return nameA.localeCompare(nameB)
        } else if (nameAConstructable) {
          return 1
        } else if (nameBConstructable) {
          return -1
        } else {
          return rowA.user.email.localeCompare(rowB.user.email)
        }
      },
      name(userObject) {
        if (is(String, userObject)) {
          return userObject
        }
        return userObject ? `${userObject.first_name} ${userObject.last_name}` : ''
      },
      formatScore(score) {
        return score ? Math.round(score) + "%" : '--';
      },
      formatDate(date) {
        if(date === undefined) return '--'
        let timeZone = this.currentOrganization.timezone;
        const zonedDate = utcToZonedTime(date, timeZone)
        return date ? format(zonedDate, DATE_FORMAT) : '--'
      },
      async handleShowAssessment(student) {
        this.showAssessmentModal = true;
        this.resetAssessmentUser();
        try {
          await this.getAssessmentUser({
            student: student,
            from: this.startDate,
            to: this.endDate,
            assessment_type: this.assessmentType
          });
        } catch {
          return warningMessage(this.$t("common.generic_error_message"))(this);
        }
      },
      canShowButton(scope) {
        const isShowBase = scope.row.last_assessment.eps && scope.row.last_assessment.eps > 0 && scope.row.user.id == this.hoveringId;
        return isShowBase;
      },
      canShowButtonAdditionalScore(scope) {
        const isShowBase = scope.row.last_assessment.overall && scope.row.last_assessment.overall > 0 && scope.row.user.id == this.hoveringId;
        return isShowBase;
      },
      handleHoveringCell(row) {
        this.hoveringId = row.user.id;
      },
      clearHoveringCell() {
        this.hoveringId = ""
      },
      percentageFormatter(row, column, cellValue, index) {
        if(cellValue == 0) return null;
        return percentage.filters.toPercentage(unless(isNil, Math.round)(cellValue));
      },
      changeAssessment(assessment) {
        this.assessmentType=assessment
        return this.getDatas({page: 1})
      }
    },
    async mounted() {
      try {
        this.getDatas({page: 1})
      } catch (error) {
        // console.error(error);
      }
    },
}
</script>

<style lang="scss" scoped>
@import '../../../scss/_colors.scss';
  .assessment-container {
    margin-top: 38px;
    .filter-sec {
      display: inline-block;
      float: left;
    }
    .export-assessment {
      display: inline-block;
      float: right;
      button {
        background: #007eff;
        border-radius: 8px;
        border: none;
        color: #fff;
        padding: 16px;
        font-size: 16px;
        line-height: 24px;
        font-family: 'SF Pro Display';
        font-weight: 600;
        max-width: 230px;
        cursor: pointer;
      }
    }
    .date-interval-sec {
      display: inline-block;
      float: right;
      margin: 0 20px;
    }
  }

  ::v-deep .elsa-input .el-select .el-input__inner {
    width:250px;
  }
  
  .user-info {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
    color: $darker-blue;
    .owner-info {
      p {
        font-weight: 500;
        font-size: 16px;
        text-transform: capitalize;
        color: $hyperlink;
        margin: 0;
        text-align: initial;
        display: flex;
        align-items: center;
        &:last-of-type {
          font-size: 14px;
          color: $light-blue-2;
          text-transform: none;
          line-height: 34px;
        }
      }
    }
  }
  .assessment-detail-btn {
    padding: 5px 5px;
    border-radius: 30px
  }
  ::v-deep .el-table .cell {
    padding: 12px 0px !important;
  }
  ::v-deep .el-table .el-table__header-wrapper .is-leaf {
      background-color: #F9FBFD !important;
      border-top: none;
  }
  ::v-deep .elsa-chip {
    color: #ffffff !important;
  }
  ::v-deep .export-assessment {
    padding-left: 25px;
    padding-right: 25px;
}
</style>
